type RawValue = unknown;

type MembershipLevelConfig = {
  level?: number;
  access?: string[];
  condition?: string;
};

export type NormalizedMembershipLevel = {
  id: string;
  planCode?: string;
  name: string;
  level?: number;
  monthlyPrice?: number;
  annualPrice?: number;
  annualMonthlyPrice?: number;
  cost?: number | string[] | null;
  setupFee?: number | null;
  description: string;
  access?: string[];
  education?: string[];
  fees?: string[];
  isPopular?: boolean;
  config?: MembershipLevelConfig;
};

const coerceString = (value: RawValue): string | undefined => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  if (typeof value === "number") {
    return value.toString();
  }

  return undefined;
};

const extractStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => {
      if (typeof item === "string") {
        return item.trim();
      }

      if (item && typeof item === "object") {
        const source = item as Record<string, unknown>;
        return (
          coerceString(
            source.label ??
              source.name ??
              source.title ??
              source.description ??
              source.text
          ) ?? ""
        );
      }

      return "";
    })
    .filter((entry) => entry.length > 0);
};

const findFirstArray = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  const container = payload as Record<string, unknown>;
  const candidates: unknown[] = [
    container.data,
    container.levels,
    container.items,
    container.result,
    container.results,
    container.payload,
    container.memberships,
    container.records,
    container.list,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }

    if (candidate && typeof candidate === "object") {
      const nested = findFirstArray(candidate);
      if (nested.length > 0) {
        return nested;
      }
    }
  }

  for (const value of Object.values(container)) {
    if (Array.isArray(value)) {
      return value;
    }
  }

  return [];
};

export const normalizeMembershipLevelsResponse = (
  payload: unknown
): NormalizedMembershipLevel[] => {
  const levelArray = findFirstArray(payload);

  if (levelArray.length === 0) {
    return [];
  }

  return levelArray.map((entry, index) => {
    const source = (entry ?? {}) as Record<string, unknown>;
    const config = (source.config ?? {}) as Record<string, unknown>;
    const monthlyPrice =
      Number(
        source.monthlyPrice ??
          source.priceMonthly ??
          source.price_monthly ??
          source.monthly_amount ??
          source.costMonthly ??
          source.price
      ) || undefined;
    const annualPrice =
      Number(
        source.annualPrice ??
          source.priceAnnual ??
          source.price_annual ??
          source.yearlyPrice ??
          source.costAnnual
      ) || undefined;
    const costRaw = source.cost ?? source.priceLabel;
    let cost: number | string[] | null = null;

    if (Array.isArray(costRaw)) {
      cost = extractStringArray(costRaw);
    } else if (typeof costRaw === "string") {
      cost = [costRaw];
    } else if (typeof costRaw === "number") {
      cost = costRaw;
    }

    return {
      id: coerceString(source.id) ?? `level-${index + 1}`,
      planCode: coerceString(source.planCode ?? source.code),
      name:
        coerceString(
          source.name ?? source.title ?? source.planCode ?? source.code
        ) ?? `Membership ${index + 1}`,
      level:
        typeof config.level === "number"
          ? config.level
          : typeof source.level === "number"
            ? source.level
            : undefined,
      monthlyPrice,
      annualPrice,
      annualMonthlyPrice:
        typeof source.annualMonthlyPrice === "number"
          ? source.annualMonthlyPrice
          : undefined,
      cost,
      setupFee:
        typeof source.setupFee === "number" ? source.setupFee : undefined,
      description:
        coerceString(source.description ?? source.summary ?? source.details) ??
        "",
      access: extractStringArray(
        config.access ?? source.access ?? source.features
      ),
      education: extractStringArray(source.education),
      fees: extractStringArray(source.fees ?? source.pricing),
      isPopular: Boolean(source.isPopular ?? source.primary),
      config: {
        ...(typeof config.level === "number" ? { level: config.level } : {}),
        access: extractStringArray(config.access),
        ...(coerceString(config.condition)
          ? { condition: coerceString(config.condition) }
          : {}),
      },
    };
  });
};
